import React, { lazy, Suspense } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { Slide, ToastContainer } from "react-toastify";
import ProtectedRoute from "../components/auth/ProtectedRoute";
import Loader from "../components/general/Loader";

// Mantine's styles first, so the app's own SCSS below wins any overlap.
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "../assets/styles/buttons.scss";
import "./App.scss";

import { useAuth } from "@hooks/useAuth";

const LoginPage = lazy(() => import("../components/auth/LoginPage"));
const NotFound = lazy(() => import("../components/general/NotFound"));
const OrdersPage = lazy(() => import("../components/orders/OrdersPage"));
const OrderPage = lazy(() => import("../components/orders/OrderPage"));
const EsimProductMappingsPage = lazy(() => import("../components/productMapping/EsimProductMappingsPage"));
const ShopifyProductsPage = lazy(() => import("../components/products/ShopifyProductsPage"));
const StatisticsPage = lazy(() => import("../components/statistics/StatisticsPage"));
const CustomersPage = lazy(() => import("../components/customers/CustomersPage"));
const CustomerPage = lazy(() => import("../components/customers/CustomerPage"));
const BannedCustomersPage = lazy(() => import("../components/banned/BannedCustomersPage"));
const SupportInboxPage = lazy(() => import("../components/support/SupportInboxPage"));
const SupportThreadPage = lazy(() => import("../components/support/SupportThreadPage"));
const KnowledgeBasePage = lazy(() => import("../components/support/KnowledgeBasePage"));
const SupportSettingsPage = lazy(() => import("../components/support/SupportSettingsPage"));
const ProviderTasksPage = lazy(() => import("../components/support/ProviderTasksPage"));

function App() {
  const { authUser, isLoading, isFetching } = useAuth();

  if (isLoading || isFetching) return null;

  return (
    <div className="blikst-app-container">
      <Router>
        <Suspense fallback={<Loader />}>
          <Routes>
            {!authUser && (
              <Route
                path="*"
                element={<LoginPage />}
              />
            )}

            <Route element={<ProtectedRoute authUser={authUser} />}>
              {authUser && (
                <Route
                  path="/"
                  element={
                    <Navigate
                      to="/orders"
                      replace
                    />
                  }
                />
              )}

              <Route
                path="/orders"
                element={<OrdersPage />}
              />

              <Route
                path="/orders/:orderId"
                element={<OrderPage />}
              />

              <Route
                path="/esim-product-mappings"
                element={<EsimProductMappingsPage />}
              />

              <Route
                path="/products"
                element={<ShopifyProductsPage />}
              />

              <Route
                path="/statistics"
                element={<StatisticsPage />}
              />

              <Route
                path="/customers"
                element={<CustomersPage />}
              />

              <Route
                path="/customers/:email"
                element={<CustomerPage />}
              />

              <Route
                path="/ban-list"
                element={<BannedCustomersPage />}
              />

              {/* Keep static /support/* segments above /support/:id. */}
              <Route
                path="/support"
                element={<SupportInboxPage />}
              />

              <Route
                path="/support/knowledge"
                element={<KnowledgeBasePage />}
              />

              <Route
                path="/support/settings"
                element={<SupportSettingsPage />}
              />

              <Route
                path="/support/provider-tasks"
                element={<ProviderTasksPage />}
              />

              <Route
                path="/support/:id"
                element={<SupportThreadPage />}
              />
            </Route>

            <Route
              path="*"
              element={authUser ? <NotFound /> : <Navigate to="/" />}
            />
          </Routes>
        </Suspense>
      </Router>

      <ToastContainer
        position="top-center"
        autoClose={5000}
        transition={Slide}
        style={{ marginTop: "50px" }}
      />
    </div>
  );
}

export default App;
